import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Edit, Trash2, MoreHorizontal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const InventarioLenceriaTable = ({ 
  inventario = [], 
  isLoading = false, 
  onEdit, 
  onDelete 
}) => {
  const getStatusBadge = (estado) => {
    const variant = estado === 'Activo' ? 'default' : 'secondary';
    const className = estado === 'Activo' 
      ? 'bg-green-100 text-green-800 hover:bg-green-200' 
      : 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    
    return (
      <Badge variant={variant} className={`text-xs ${className}`}>
        {estado}
      </Badge>
    );
  };

  const formatPeso = (peso) => {
    return `${peso} kg`;
  };

  const handleAction = (action, item) => {
    switch (action) {
      case 'edit':
        onEdit?.(item);
        break;
      case 'delete':
        onDelete?.(item);
        break;
      default:
        console.warn('Acción no reconocida:', action);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100 rounded-t-lg"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-50 border-t border-gray-100"></div>
          ))}
        </div>
      </div>
    );
  }

  if (inventario.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-12 text-center">
        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No hay elementos en el inventario
        </h3>
        <p className="text-gray-500">
          Comienza agregando elementos de lencería al inventario
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
          <div className="col-span-4">Elemento</div>
          <div className="col-span-2">Peso</div>
          <div className="col-span-2">Cantidad</div>
          <div className="col-span-2">Estado</div>
          <div className="col-span-1">Propiedad</div>
          <div className="col-span-1 text-center">Acciones</div>
        </div>
      </div>

      {/* Body */}
      <div className="divide-y divide-gray-200">
        <AnimatePresence>
          {inventario.map((item, index) => (
            <motion.div
              key={item._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2, delay: index * 0.02 }}
              className="px-6 py-4 hover:bg-gray-50 transition-colors duration-200"
            >
              <div className="grid grid-cols-12 gap-4 items-center">
                {/* Elemento */}
                <div className="col-span-4">
                  <div className="flex items-center space-x-3">
                    <Package className="h-5 w-5 text-blue-600" />
                    <div>
                      <div className="font-medium text-gray-900">
                        {item.nombre}
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.descripcion || 'Sin descripción'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Peso */}
                <div className="col-span-2">
                  <span className="text-sm font-medium text-gray-900">
                    {formatPeso(item.peso)}
                  </span>
                </div>

                {/* Cantidad */}
                <div className="col-span-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg font-bold text-gray-900">
                      {item.cantidad}
                    </span>
                    <span className="text-sm text-gray-500">
                      unidades
                    </span>
                  </div>
                </div>

                {/* Estado */}
                <div className="col-span-2">
                  {getStatusBadge(item.estado)}
                </div>

                {/* Propiedad */}
                <div className="col-span-1">
                  <div className="text-sm text-gray-600 truncate" title={item.propiedad?.nombre}>
                    {item.propiedad?.nombre || 'N/A'}
                  </div>
                </div>

                {/* Acciones */}
                <div className="col-span-1 text-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-gray-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Abrir menú</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem
                        onClick={() => handleAction('edit', item)}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <Edit className="h-4 w-4" />
                        <span>Editar</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleAction('delete', item)}
                        className="flex items-center space-x-2 cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Eliminar</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Footer info */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
        <div className="text-sm text-gray-600">
          Mostrando {inventario.length} elementos
        </div>
      </div>
    </div>
  );
};

export default InventarioLenceriaTable;
